export const RULE_GROUPS = [
  "Source health",
  "Cadence tiers",
  "Status sentence",
  "Appearances as speaker",
  "Task priority",
  "Stale verification",
  "Attempts",
  "Dependencies",
] as const;
export type RuleGroup = (typeof RULE_GROUPS)[number];

export interface RuleDefinition {
  readonly rule: string;
  readonly group: RuleGroup;
  readonly label: string;
  readonly initial: number;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly unit: string;
  readonly usedIn: string;
}

const rule = (
  group: RuleGroup,
  r: string,
  label: string,
  initial: number,
  unit: string,
  usedIn: string,
  min = 0,
  max = 1000,
  step = 1,
): RuleDefinition => ({
  rule: r,
  group,
  label,
  initial,
  min,
  max,
  step,
  unit,
  usedIn,
});

export const PRIORITY_WEIGHT_RULES = {
  required_gap: "priority.weight_required_gap",
  platform_issue: "priority.weight_platform_issue",
  failed_items: "priority.weight_failed_items",
  gap: "priority.weight_gap",
  reported_problem: "priority.weight_reported_problem",
  identity: "priority.weight_identity",
  suggested_sources: "priority.weight_suggested_sources",
  plan_review: "priority.weight_plan_review",
} as const;

export const RULES: readonly RuleDefinition[] = [
  rule(
    "Source health",
    "health.late_multiplier",
    "Late after this many cadence intervals without a scan",
    2,
    "× cadence",
    "Sources, Home",
    1,
    10,
  ),
  rule(
    "Source health",
    "health.quiet_multiplier",
    "Unusually quiet after this many usual publication gaps",
    3,
    "× usual gap",
    "Sources, Home",
    1,
    10,
  ),
  rule("Source health", "health.quiet_min_days", "…and at least this many days", 7, "days", "Sources, Home", 1, 90),
  rule(
    "Source health",
    "health.quiet_window",
    "Usual gap is the median of the last publications",
    10,
    "publications",
    "Sources",
    3,
    50,
  ),
  rule(
    "Source health",
    "health.quiet_min_publications",
    "Known publications needed before a source can be quiet",
    5,
    "publications",
    "Sources",
    2,
    50,
  ),
  rule("Cadence tiers", "cadence.priority_hours", "Priority: scan every", 2, "h", "Sources, plans", 1, 24),
  rule("Cadence tiers", "cadence.priority_target_hours", "Priority: found within", 4, "h", "Sources, Home", 1, 48),
  rule("Cadence tiers", "cadence.standard_hours", "Standard: scan every", 12, "h", "Sources, plans", 1, 72),
  rule("Cadence tiers", "cadence.standard_target_hours", "Standard: found within", 24, "h", "Sources, Home", 1, 168),
  rule("Cadence tiers", "cadence.low_hours", "Low: scan every", 24, "h", "Sources, plans", 1, 168),
  rule("Cadence tiers", "cadence.low_target_hours", "Low: found within", 48, "h", "Sources, Home", 1, 336),
  rule(
    "Status sentence",
    "status.disrupted_hours",
    "Disrupted when scans or downloads stall for",
    6,
    "h",
    "Home, status bar",
    1,
    48,
  ),
  rule(
    "Status sentence",
    "status.disrupted_issues",
    "Disrupted from this many issues",
    3,
    "issues",
    "Home, status bar",
    1,
    20,
  ),
  rule(
    "Status sentence",
    "status.with_issues",
    "With issues from this many issues",
    1,
    "issues",
    "Home, status bar",
    1,
    20,
  ),
  rule(
    "Appearances as speaker",
    "appearances.min_confidence",
    "Counted from attribution confidence",
    0.8,
    "",
    "Profiles",
    0,
    1,
    0.01,
  ),
  rule(
    "Task priority",
    PRIORITY_WEIGHT_RULES.required_gap,
    "Type weight: required-route gap",
    40,
    "points",
    "Tasks",
    0,
    100,
  ),
  rule(
    "Task priority",
    PRIORITY_WEIGHT_RULES.platform_issue,
    "Type weight: platform-wide cause, quarantined route, budget alert",
    35,
    "points",
    "Tasks",
    0,
    100,
  ),
  rule(
    "Task priority",
    PRIORITY_WEIGHT_RULES.failed_items,
    "Type weight: failed or waiting items",
    25,
    "points",
    "Tasks",
    0,
    100,
  ),
  rule("Task priority", PRIORITY_WEIGHT_RULES.gap, "Type weight: other gaps", 20, "points", "Tasks", 0, 100),
  rule(
    "Task priority",
    PRIORITY_WEIGHT_RULES.reported_problem,
    "Type weight: reported problems, matches, carry-over, optional reviews",
    20,
    "points",
    "Tasks",
    0,
    100,
  ),
  rule(
    "Task priority",
    PRIORITY_WEIGHT_RULES.identity,
    "Type weight: duplicate or identity checks",
    15,
    "points",
    "Tasks",
    0,
    100,
  ),
  rule(
    "Task priority",
    PRIORITY_WEIGHT_RULES.suggested_sources,
    "Type weight: suggested sources, draft profiles",
    10,
    "points",
    "Tasks",
    0,
    100,
  ),
  rule("Task priority", PRIORITY_WEIGHT_RULES.plan_review, "Type weight: plan reviews", 5, "points", "Tasks", 0, 100),
  rule("Task priority", "priority.age_per_day", "Age: points per day waiting", 2, "points", "Tasks", 0, 20),
  rule("Task priority", "priority.age_max", "Age: at most", 30, "points", "Tasks", 0, 100),
  rule("Task priority", "priority.per_person", "Impact: points per affected person", 5, "points", "Tasks", 0, 50),
  rule("Task priority", "priority.people_max", "Impact from people: at most", 20, "points", "Tasks", 0, 100),
  rule("Task priority", "priority.items_per_point", "Impact: affected items per point", 5, "items", "Tasks", 1, 100),
  rule("Task priority", "priority.items_max", "Impact from items: at most", 10, "points", "Tasks", 0, 100),
  rule("Task priority", "priority.high_from", "High from", 60, "points", "Tasks", 1, 300),
  rule("Task priority", "priority.medium_from", "Medium from", 30, "points", "Tasks", 1, 300),
  rule(
    "Stale verification",
    "verification.account_months",
    "Account relationships are stale after",
    1,
    "months",
    "Profiles",
    1,
    24,
  ),
  rule(
    "Stale verification",
    "verification.identity_months",
    "Other identity facts are stale after",
    3,
    "months",
    "Profiles",
    1,
    24,
  ),
  rule(
    "Attempts",
    "attempts.automatic_per_stage",
    "Automatic attempts per stage, across routes",
    3,
    "attempts",
    "Backlog, retries",
    1,
    10,
  ),
  rule(
    "Dependencies",
    "dependencies.expiry_warning_days",
    "Credential expiry and plan renewal: warn this many days ahead",
    30,
    "days",
    "Settings › Routes, Tasks",
    1,
    365,
  ),
];

export const RULE_BY_KEY: ReadonlyMap<string, RuleDefinition> = new Map(RULES.map((r) => [r.rule, r]));

export type RuleValues = ReadonlyMap<string, number>;

export function initialRuleValues(): Map<string, number> {
  return new Map(RULES.map((r) => [r.rule, r.initial]));
}

export function ruleValue(values: RuleValues, key: string): number {
  const value = values.get(key) ?? RULE_BY_KEY.get(key)?.initial;
  if (value === undefined) throw new Error(`Unknown rule ${key}`);
  return value;
}

export function validateRuleValue(def: Pick<RuleDefinition, "min" | "max" | "step">, value: number): string | null {
  if (!Number.isFinite(value)) return "Enter a number";
  if (value < def.min || value > def.max) return `Enter a value from ${String(def.min)} to ${String(def.max)}`;
  if (def.step >= 1 && !Number.isInteger(value)) return "Enter a whole number";
  return null;
}
